import React, { useEffect, useRef, useState } from "react";
import { Job } from "../../../../Entities/Job";
import { PaginationType } from "../../../../Interface";
import {
  Avatar,
  Button,
  Empty,
  Flex,
  List,
  Pagination,
  Table,
  TableColumnsType,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

interface FileUploadsJobApplicationListingProps {
  jobs: Job[];
  pagination: PaginationType;
  onPaginationChange: (page: number, pageSize?: number) => void;
  jobDescriptionId: string;
}

const processSteps = [
    {
      title: "Upload Candidate Resume",
      description:
        "Admins can upload a candidate's resume file, and the system will automatically extract and save the candidate's details.",
    },
    {
      title: "AI Profile Matching",
      description:
        "The extracted candidate details will be matched against the job requirements using AI to determine the suitability.",
    },
    {
      title: "AI-Powered Interview",
      description:
        "Qualified candidates will automatically receive an email inviting them to participate in an AI-driven interview.",
    },
  ];
  
const FileUploadsJobApplicationListing: React.FC<
  FileUploadsJobApplicationListingProps
> = ({ jobs, pagination, onPaginationChange, jobDescriptionId }) => {
  const [tableScrollHeight, setTableScrollHeight] = useState(400);
  const tableRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const handleViewClick = (applicationId: string | undefined) => {
    if (!applicationId) return;
    // Encode current URL to preserve state on back navigation
    const currentUrl = window.location.pathname + window.location.search;
    const encodedReturnUrl = encodeURIComponent(currentUrl);
    navigate(
      `/application-details?applicationId=${applicationId}&jobId=${jobDescriptionId}&returnUrl=${encodedReturnUrl}`
    );
  };

  const getFailureReason = (failureReason: Job["failureReason"] | null) => {
    if (!failureReason) return "";
    switch (failureReason) {
      case "application.limit.reached":
        return "Application Limit Reached";
      case "application.already.exists":
        return "Application Already Exists";
      case "failed.to.extract.user.details":
        return "Failed to Extract User Details";
      case "invalid.file.format":
        return "Invalid File Format";
      case "unable.to.process.resume":
        return "Unable to Process Resume";
      default:
        return failureReason;
    }
  };

  const getStatus = (status: Job["status"]) => {
    switch (status) {
      case "pending":
        return {
          color: "blue",
          label: "Pending",
        };
      case "completed":
        return {
          color: "green",
          label: "Completed",
        };
      case "failed":
        return {
          color: "red",
          label: "Failed",
        };
      default:
        return {
          color: "default",
          label: "",
        };
    }
  };

  const columns: TableColumnsType<Job> = [
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      minWidth: 150,
      render: (data) => {
        return (
          <Flex vertical gap={2}>
            <Typography.Text>
              {dayjs(data).format("DD MMM YYYY")}
            </Typography.Text>
            <Typography.Text type="secondary">
              {dayjs(data).format("hh:mm A")}
            </Typography.Text>
          </Flex>
        );
      },
    },
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "fileName",
      minWidth: 200,
      render: (data) => {
        return (
          <Typography.Text
            ellipsis={{ tooltip: data }}
            style={{ maxWidth: "200px", cursor: "pointer" }}
          >
            {data}
          </Typography.Text>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      minWidth: 120,
      render: (data, record) => {
        return (
          <Tag color={getStatus(record.status).color}>
            {getStatus(record.status).label}
          </Tag>
        );
      },
    },
    {
      title: "Failure Reason",
      dataIndex: "failureReason",
      key: "failureReason",
      minWidth: 200,
      render: (data) => (
        <Typography.Text type={!data ? "secondary" : "danger"}>
          {getFailureReason(data || null)}
        </Typography.Text>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      minWidth: 150,
      render: (data, record) =>
        record?.jobApplicationId ? (
          <Button
            icon={<EyeOutlined />}
            type="link"
            onClick={() => handleViewClick(record.jobApplicationId)}
          />
        ) : (
          <Typography.Text type="secondary">-</Typography.Text>
        ),
    },
  ];

  useEffect(() => {
    const updateTableHeight = () => {
      if (tableRef.current) {
        const computedStyle = getComputedStyle(tableRef.current);
        const heightValue = computedStyle.getPropertyValue(
          "--job-listing-table-scroll-height"
        );
        if (heightValue) {
          const heightMatch = heightValue.match(
            /calc\([^)]*100\s*-\s*(\d+)px\)/
          );
          if (heightMatch) {
            const vh = window.innerHeight;
            const offset = parseInt(heightMatch[1]);
            const calculatedHeight = vh - offset;
            setTableScrollHeight(Math.max(calculatedHeight, 200));
          }
        }
      }
    };

    updateTableHeight();

    window.addEventListener("resize", updateTableHeight);

    return () => {
      window.removeEventListener("resize", updateTableHeight);
    };
  }, []);

  const totalMinWidth = columns.reduce((total, column) => {
    return total + (column.minWidth || 100);
  }, 0);

  return (
    <section className="file-uploads-job-listing">
      {jobs && jobs.length > 0 ? (
        <>
          <div className="file-uploads-job-listing-table" ref={tableRef}>
            <Table
              columns={columns}
              dataSource={jobs}
              rowKey="_id"
              size="small"
              pagination={false}
              scroll={{
                x: totalMinWidth,
                y: tableScrollHeight,
              }}
            />
          </div>
          <div className="file-uploads-job-listing-pagination">
            <Pagination
              current={pagination.page}
              pageSize={pagination.limit}
              total={pagination.totalRecords}
              onChange={onPaginationChange}
              showTotal={(total, range) =>
                `Showing ${range[0]}-${range[1]} of ${total} items`
              }
            />
          </div>
        </>
      ) : (
        <section className="bulk-upload-listing-empty">
          <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={(
              <Flex vertical align="center">
                <List
                  itemLayout="vertical"
                  style={{ width: '100%', maxWidth: '600px' }}
                  dataSource={processSteps}
                  renderItem={(item: any, index: number) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<Avatar>{index + 1}</Avatar>}
                        title={item.title}
                        description={item.description}
                        style={{ textAlign: "left" }}
                      />
                    </List.Item>
                  )}
                />
              </Flex>
            )}
            // image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ width: '100%' }}
          />
        </section>
      )}
    </section>
  );
};

export default FileUploadsJobApplicationListing;
